import React, { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import api, { apiErr } from "../../lib/api";
import { useLang } from "../../i18n";
import { PhaseIntro, RevisitCallout } from "../../components/phase-common";
import DelftTile from "../../components/DelftTile";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Trash2, CheckCircle2, Circle, Download } from "lucide-react";

export default function Phase4({ org, refresh }) {
  const { t, lang } = useLang();
  const [l1, setL1] = useState("");
  const [l2, setL2] = useState("");
  const watermark = !org.is_paid;
  const tiles = org.tiles || [];
  const tileRefs = useRef({});

  const download = async (tile) => {
    const node = tileRefs.current[tile.id];
    if (!node) return;
    try {
      const dataUrl = await toPng(node, { pixelRatio: 3, cacheBust: true });
      const a = document.createElement("a");
      const slug = (tile.line1 || "tegeltje").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
      a.download = `core-emotion-${slug || "tegeltje"}.png`;
      a.href = dataUrl;
      a.click();
    } catch (e) {
      toast.error(lang === "nl" ? "Download mislukt" : "Download failed");
    }
  };

  const topClusters = useMemo(() => {
    const map = {};
    (org.feelings || []).forEach((f) => { const c = f.cluster || "Overig"; map[c] = (map[c] || 0) + (f.count || 1); });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [org.feelings]);

  const save = async () => {
    if (!l1.trim()) return;
    try {
      await api.post("/org/tiles", { line1: l1.trim(), line2: l2.trim() });
      setL1(""); setL2(""); refresh();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleSelect = async (tile) => {
    try { await api.patch(`/org/tiles/${tile.id}`, { selected: !tile.selected }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/org/tiles/${id}`); refresh(); };

  const selectedCount = tiles.filter((x) => x.selected).length;

  return (
    <div className="space-y-6">
      <PhaseIntro phase={4} title={t("p4_title")} intro={t("p4_intro")} />
      <RevisitCallout coreEmotion={org.core_emotion} subject={t("p4_subject")} />

      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-lg font-semibold text-primary">{t("top_clusters")}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {topClusters.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
          {topClusters.map(([c, n]) => (
            <span key={c} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold text-secondary-foreground">
              {c} <span className="ml-1 text-primary">· {n}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <h3 className="font-display text-lg font-semibold text-primary">{lang === "nl" ? "Nieuw tegeltje" : "New tile"}</h3>
          <div className="space-y-1.5">
            <Label>{t("tile_line1")}</Label>
            <Input data-testid="p4-line1" value={l1} onChange={(e) => setL1(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("tile_line2")}</Label>
            <Input data-testid="p4-line2" value={l2} onChange={(e) => setL2(e.target.value)} />
          </div>
          <Button data-testid="p4-save-tile" onClick={save} className="rounded-full">{t("save_tile")}</Button>
        </div>
        <div className="mx-auto w-full max-w-xs">
          <DelftTile line1={l1 || (lang === "nl" ? "Jouw wijsheid" : "Your wisdom")} line2={l2} watermark={watermark} testId="p4-preview" />
        </div>
      </div>

      <div>
        <h3 className="mb-4 font-display text-lg font-semibold text-primary">
          {lang === "nl" ? "Opgeslagen tegeltjes" : "Saved tiles"} · {selectedCount}/2 {t("selected").toLowerCase()}
        </h3>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <div key={tile.id} className="space-y-2">
              <DelftTile innerRef={(el) => (tileRefs.current[tile.id] = el)}
                line1={tile.line1} line2={tile.line2} watermark={watermark} testId={`tile-${tile.id}`} />
              <div className="flex items-center justify-between gap-2">
                <Button data-testid={`tile-select-${tile.id}`} size="sm" variant={tile.selected ? "default" : "outline"}
                  onClick={() => toggleSelect(tile)} className="rounded-full">
                  {tile.selected ? <CheckCircle2 className="mr-1.5 h-4 w-4" /> : <Circle className="mr-1.5 h-4 w-4" />}
                  {tile.selected ? t("selected") : t("select_tile")}
                </Button>
                <div className="flex items-center gap-1">
                  <button data-testid={`tile-download-${tile.id}`} onClick={() => download(tile)}
                    aria-label="download" className="rounded-full p-2 text-primary transition-colors hover:bg-secondary" title={t("download")}>
                    <Download className="h-4 w-4" />
                  </button>
                  <button data-testid={`tile-delete-${tile.id}`} onClick={() => del(tile.id)} aria-label="delete"
                    className="rounded-full p-2 transition-colors hover:bg-secondary">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
